import * as React from 'react'

import { clsx } from 'clsx'

import {
  Text,
  Flex,
  IconButton,
  Kbd,
  LayoutGridIcon,
  GalleryVerticalEndIcon,
  ChevronsRightIcon,
  PanelLeftCloseIcon,
  Tooltip,
  useIsMobileScreenSize,
} from '@buffer-mono/popcorn'

import type { FragmentOf } from '~publish/graphql'

import { PostsList } from './PostsList'
import { ReorderPostsButton } from './ReorderPostsButton'
import {
  Sidepanel,
  useSidepanelCollapsible,
} from '../../../components/Sidepanel'
import type { UsePostsQueryResult } from '../hooks/usePostsQuery'
import { useViewMode } from '../hooks/useViewMode'
import type { UseViewMode_Channel } from '../hooks/useViewMode'
import type { PostListItem, ViewMode } from '../types'

import styles from './PostsSidepanel.module.css'

import type { JSX } from 'react'

type PostsSidepanelProps = {
  posts: PostListItem[]
  selectedPostId?: string
  onPostSelect: (postId: string) => void
  lastElementRef: UsePostsQueryResult['lastElementRef']
  fetchingMore: boolean
  loading: boolean
  channels: FragmentOf<typeof UseViewMode_Channel>[]
  isMultiChannel: boolean
  isPinned: boolean
  onPinnedChange: (pinned: boolean) => void
  showOnlyPostsWithComments?: boolean
  onShowOnlyPostsWithCommentsChange?: (value: boolean) => void
  onShowAllPosts?: () => void
  /**
   * Reloads the posts list from its first page, resolving with the query's
   * result. Required, because the pill this panel renders reads that result to
   * decide whether the comments it announced were covered, and an absent
   * reload would read as one that succeeded.
   */
  onRefreshPosts: () => Promise<unknown>
  /** When true, the empty state uses the no-channels-connected copy. */
  noChannelsConnected?: boolean
}

export function PostsSidepanel({
  posts,
  selectedPostId,
  onPostSelect,
  lastElementRef,
  fetchingMore,
  loading,
  channels,
  isMultiChannel,
  isPinned,
  onPinnedChange,
  showOnlyPostsWithComments,
  onShowOnlyPostsWithCommentsChange,
  onShowAllPosts,
  onRefreshPosts,
  noChannelsConnected = false,
}: PostsSidepanelProps): JSX.Element {
  const { viewMode, setViewMode, isGridViewAvailable } = useViewMode({
    posts,
    channels,
  })

  const { isPreview, closePreview } = useSidepanelCollapsible()
  const isMobile = useIsMobileScreenSize()

  const handlePostSelect = React.useCallback(
    (postId: string) => {
      if (isPreview) {
        closePreview()
      }
      if (isMobile) {
        onPinnedChange(false)
      }
      onPostSelect(postId)
    },
    [isPreview, closePreview, isMobile, onPinnedChange, onPostSelect],
  )

  const contentRef = React.useRef<HTMLDivElement>(null)
  const [isScrolled, setIsScrolled] = React.useState(false)

  React.useEffect(() => {
    const el = contentRef.current
    if (!el) return undefined

    const handleScroll = (): void => {
      setIsScrolled(el.scrollTop > 0)
    }

    handleScroll()
    el.addEventListener('scroll', handleScroll, { passive: true })
    return (): void => el.removeEventListener('scroll', handleScroll)
  }, [])

  const handleRefreshPosts = React.useCallback(async (): Promise<unknown> => {
    // The reload returns the first page only. Going back to the top of the
    // list first keeps the infinite-scroll sentinel out of view, so the panel
    // does not immediately page the older order back in over it.
    if (contentRef.current) {
      contentRef.current.scrollTop = 0
    }
    // Handed back, not swallowed: the pill reads it to tell a reload that
    // landed from one that resolved carrying a failure.
    return await onRefreshPosts()
  }, [onRefreshPosts])

  return (
    <Sidepanel
      label="Posts list"
      size="large"
      collapsible
      open={isPinned}
      onOpenChange={onPinnedChange}
      data-empty={
        noChannelsConnected || (!loading && posts.length === 0) || undefined
      }
    >
      <PostsSidepanelHeader
        isPinned={isPinned}
        onPinnedChange={onPinnedChange}
        viewMode={viewMode}
        setViewMode={setViewMode}
        isGridViewAvailable={isGridViewAvailable}
        isScrolled={isScrolled}
        showOnlyPostsWithComments={showOnlyPostsWithComments}
        onShowOnlyPostsWithCommentsChange={onShowOnlyPostsWithCommentsChange}
        onReorderPosts={handleRefreshPosts}
      />
      <Sidepanel.Content ref={contentRef}>
        <PostsList
          posts={posts}
          selectedPostId={selectedPostId}
          onPostSelect={handlePostSelect}
          lastElementRef={lastElementRef}
          fetchingMore={fetchingMore}
          loading={loading}
          isMultiChannel={isMultiChannel}
          viewMode={viewMode}
          showOnlyPostsWithComments={showOnlyPostsWithComments}
          onShowAllPosts={onShowAllPosts}
          noChannelsConnected={noChannelsConnected}
        />
      </Sidepanel.Content>
    </Sidepanel>
  )
}

type PostsSidepanelHeaderProps = {
  isPinned: boolean
  onPinnedChange: (pinned: boolean) => void
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  isGridViewAvailable: boolean
  isScrolled?: boolean
  showOnlyPostsWithComments?: boolean
  onShowOnlyPostsWithCommentsChange?: (value: boolean) => void
  /** Reloads the posts list from its first page, resolving with the result. */
  onReorderPosts: () => Promise<unknown>
}

function PostsSidepanelHeader({
  isPinned,
  onPinnedChange,
  viewMode,
  setViewMode,
  isGridViewAvailable,
  isScrolled = false,
  showOnlyPostsWithComments,
  onShowOnlyPostsWithCommentsChange,
  onReorderPosts,
}: PostsSidepanelHeaderProps): JSX.Element {
  // In the hover preview the content padding is removed, so drop the header's
  // matching right padding too — otherwise the trailing icon sits inset from
  // the flush post cards.
  const { isPreview } = useSidepanelCollapsible()

  // The button is "on" (green) when all posts are shown; off by default while
  // the list is filtered to posts with comments. The tooltip states the action
  // a click performs, so it reads as the opposite of the current state.
  const showAllPosts = !showOnlyPostsWithComments
  const showAllPostsLabel = showOnlyPostsWithComments
    ? 'Show all posts, including those with no comments'
    : 'Show only posts with comments'

  return (
    <Flex
      align="center"
      justify="between"
      fullWidth
      className={clsx(
        styles.postsHeader,
        isScrolled && styles.postsHeaderScrolled,
        isPreview && styles.postsHeaderPreview,
      )}
    >
      <Tooltip
        content={
          <>
            Navigate with <Kbd color="inverted">mod</Kbd>{' '}
            <Kbd color="inverted">↑</Kbd> <Kbd color="inverted">↓</Kbd>
          </>
        }
      >
        <Text size="md" weight="medium" color="subtle">
          Posts
        </Text>
      </Tooltip>
      <Flex align="center" gap="xs">
        <ReorderPostsButton onReorder={onReorderPosts} />
        <Tooltip content={showAllPostsLabel}>
          <IconButton
            label={showAllPostsLabel}
            variant={showAllPosts ? 'primary' : 'tertiary'}
            size="small"
            onClick={() =>
              onShowOnlyPostsWithCommentsChange?.(!showOnlyPostsWithComments)
            }
            className={
              showAllPosts ? styles.showAllPostsToggleActive : undefined
            }
          >
            <GalleryVerticalEndIcon />
          </IconButton>
        </Tooltip>
        {isGridViewAvailable && (
          <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
        )}
        <Tooltip
          content={
            <>
              {isPinned ? 'Collapse panel' : 'Pin panel'}{' '}
              <Kbd color="inverted">mod</Kbd> <Kbd color="inverted">shift</Kbd>{' '}
              <Kbd color="inverted">P</Kbd>
            </>
          }
        >
          <IconButton
            label={isPinned ? 'Collapse posts panel' : 'Pin posts panel'}
            variant="tertiary"
            size="small"
            onClick={() => onPinnedChange(!isPinned)}
          >
            {isPinned ? <PanelLeftCloseIcon /> : <ChevronsRightIcon />}
          </IconButton>
        </Tooltip>
      </Flex>
    </Flex>
  )
}

type ViewModeToggleProps = {
  viewMode: ViewMode
  onViewModeChange: (mode: ViewMode) => void
}

function ViewModeToggle({
  viewMode,
  onViewModeChange,
}: ViewModeToggleProps): JSX.Element {
  const isGridView = viewMode === 'grid'

  return (
    <Tooltip
      content={isGridView ? 'Switch to list view' : 'Switch to grid view'}
    >
      <IconButton
        label={isGridView ? 'Switch to list view' : 'Switch to grid view'}
        variant={isGridView ? 'primary' : 'secondary'}
        onClick={() => onViewModeChange(isGridView ? 'list' : 'grid')}
        size="small"
        className={isGridView ? styles.gridToggleActive : styles.gridToggle}
      >
        <LayoutGridIcon color="subtle" />
      </IconButton>
    </Tooltip>
  )
}
